import React from "react";
import {useKeycloak} from "@react-keycloak/web";
import {useTranslation} from "react-i18next";
// Material UI imports
import {
    Box,
    Container,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
// Local imports
import DataSourceNew from "./DataSourceNew";
// Project imports
import {AddCircleActionButton, EditLinkActionButton, IdCopyButton, RefreshActionButton} from "../../common/buttons";
import {ServerCommunicationAlert} from "../../common/errors";
import {Loading} from "../../common/Loading";
import {InfoNotification} from "../../common/notifications";
import {DataSourceDTO} from "../../../models/library";
import ROUTES from "../../../routes/routes";
import {useStyles} from "../../../styles";
import {API, createApiConfig} from "../../../utils";
import {AxiosError} from "axios";
import {Page} from "../../../models/pagination";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";

interface DataSourceRowProps {
    dataSource: DataSourceDTO;
}

const DataSourceRow: React.FC<DataSourceRowProps> = (props: DataSourceRowProps) => {
    return (
        <TableRow>
            <TableCell>{props.dataSource.name}</TableCell>
            <TableCell align="right">
                <IdCopyButton text={props.dataSource.id} size="small" />
                <EditLinkActionButton to={ROUTES.dataSource.create(props.dataSource.id)} size="small" />
            </TableCell>
        </TableRow>
    );
};

export const DataSourcesTable: React.FC = () => {
    //== Init ===================================================================
    const [t] = useTranslation("libraries");
    const classes = useStyles();
    const {keycloak, initialized} = useKeycloak();
    const initState = {
        page: {
            size: 20,
            totalElements: 0,
            totalPages: 0,
            number: 0,
        },
        query: "",
        dataSources: [] as DataSourceDTO[],
        name: "loading",
        action: "init",
    };
    const [state, setState] = React.useState(initState);
    //== Effects ================================================================
    React.useEffect(() => {
        if (state.name === "loading") {
            let url = `/data-sources?page=${state.page.number}&size=${state.page.size}&sort=name,asc`;
            if (state.query !== "") {
                url += `&query=${state.query}`;
            }
            API.get<Page<DataSourceDTO>>(url, createApiConfig(keycloak, initialized))
                .then((res) => {
                    setState({
                        ...state,
                        page: res.data,
                        dataSources: res.data?.content ?? [],
                        name: "loaded",
                    });
                })
                .catch((err: AxiosError) => {
                    setState({...state, name: "failed"});
                });
        }
    }, [keycloak, initialized, state, setState]);
    //== Handlers ===============================================================
    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number): void => {
        setState({
            ...state,
            page: {...state.page, number: value - 1},
            name: "loading",
            action: "init",
        });
    };
    const handleQueryChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        setState({...state, query: event.target.value});
    };
    const handleSearchSubmit = (): void => {
        setState({...state, name: "loading"});
    };
    const handleCreateClick = (): void => setState({...state, name: "creating"});
    //== Render =================================================================
    if (state.name === "loading") {
        return <Loading />;
    }
    if (state.name === "creating") {
        return <DataSourceNew />;
    }
    if (state.name === "failed") {
        return <ServerCommunicationAlert />;
    }
    // - default
    let notification: JSX.Element | null = null;
    if (state.name === "loaded" && state.action === "refresh") {
        notification = <InfoNotification message={t("definitions.data_sources.notifications.refresh_ok")} />;
    }
    const rows = state.dataSources.map((dataSource: DataSourceDTO) => (
        <DataSourceRow key={dataSource.id} dataSource={dataSource} />
    ));
    return (
        <Container maxWidth="lg">
            <Box display="flex" justifyContent="center">
                <Box flexGrow={1}>
                    {state.page.totalPages > 1 && (
                        <Pagination
                            style={{marginTop: "1em"}}
                            count={state.page.totalPages}
                            page={state.page.number + 1}
                            onChange={handlePageChange}
                        />
                    )}
                </Box>
                <form onSubmit={handleSearchSubmit} style={{width: "auto"}}>
                    <Box display="flex" alignItems="center" justifyContent="center">
                        <TextField
                            value={state.query}
                            onChange={handleQueryChange}
                            label={t("definitions.data_sources.query")}
                            margin="dense"
                            variant="outlined"
                            style={{minWidth: "20em", marginRight: "1em"}}
                        />
                        <IconButton onClick={handleSearchSubmit} size="small">
                            <SearchIcon />
                        </IconButton>
                        <IconButton onClick={handleCreateClick} size="small">
                            <AddIcon />
                        </IconButton>
                    </Box>
                </form>
            </Box>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell align="left">{t("definitions.data_sources.name")}</TableCell>
                        <TableCell align="right" style={{minWidth: "8em"}}>
                            <RefreshActionButton
                                size="small"
                                onClick={(): void =>
                                    setState({
                                        ...initState,
                                        action: "refresh",
                                    })
                                }
                            />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>{rows}</TableBody>
            </Table>
            {state.page.totalPages > 1 && (
                <Pagination
                    style={{marginTop: "1em"}}
                    count={state.page.totalPages}
                    page={state.page.number + 1}
                    onChange={handlePageChange}
                />
            )}
            {notification}
            <div className={classes.fabWrapper}>
                <AddCircleActionButton onClick={(): void => setState({...state, name: "creating"})} />
            </div>
        </Container>
    );
};

export default DataSourcesTable;
